import type { Event, PermissionRequest, QuestionRequest } from "@mux/sdk/v2/client"
import { batch, createSignal } from "solid-js"
import { reconcile, type SetStoreFunction, type Store } from "solid-js/store"
import { askedRequestRoute, REQUEST_ROUTE_EVENT, type OpenRequests } from "@/transport/requests"
import type { GlobalStore } from "./types"
import { cmp } from "./utils"
import { applyRequestEvent } from "./request-event-reducer"

type Mutation<T> = { revision: number; sessionID: string; requestID: string; request?: T }
type Change =
  | ({ kind: "permission" } & Mutation<PermissionRequest>)
  | ({ kind: "question" } & Mutation<QuestionRequest>)

function bySession<T extends { sessionID: string }>(requests: readonly T[]) {
  const grouped: Record<string, T[]> = {}
  for (const request of requests) (grouped[request.sessionID] ??= []).push(request)
  return grouped
}

function applyMutation<T extends { id: string }>(grouped: Record<string, T[]>, change: Mutation<T>) {
  const requests = (grouped[change.sessionID] ??= [])
  const index = requests.findIndex((item) => item.id === change.requestID)
  if (!change.request) {
    if (index !== -1) requests.splice(index, 1)
  } else if (index === -1) {
    requests.push(change.request)
  } else {
    requests[index] = change.request
  }
}

const sorted = <T extends { id: string }>(requests: T[] | undefined) => (requests ?? []).sort((a, b) => cmp(a.id, b.id))

// Whether an asked request brings a route the reducer will apply; an invalid
// one is ignored there, so it must not hold back the snapshot's route.
const carriesRoute = (request: object) => {
  try {
    return askedRequestRoute(request) !== undefined
  } catch {
    return false
  }
}

// How many answered question IDs are kept to drop a late replay of their ask.
const RESOLVED_QUESTIONS_KEPT = 1000

/**
 * The machine's open questions and permission asks, and where each asking
 * chat's requests surface. Summary events keep them current on every page; a
 * snapshot replaces them after a gap, keeping whatever changed while it loaded.
 */
export function createRequestSnapshotController(input: {
  store: Store<GlobalStore>
  setStore: SetStoreFunction<GlobalStore>
}) {
  let revision = 0
  let latest = 0
  // Refreshes still loading. Changes are kept only while one is, to apply
  // over its snapshot.
  let loading = 0
  const changes = new Map<string, Change>()
  const [confirmed, setConfirmed] = createSignal(false)
  // When each asking chat's route last arrived, so an older snapshot cannot
  // replace a newer route.
  const routed = new Map<string, number>()
  // Request IDs are never reused, so an answered question stays answered. The
  // oldest are forgotten first; a replay arrives long before that.
  const resolvedQuestions = new Set<string>()
  const resolved = (id: string) => {
    resolvedQuestions.delete(id)
    resolvedQuestions.add(id)
    if (resolvedQuestions.size > RESOLVED_QUESTIONS_KEPT) {
      resolvedQuestions.delete(resolvedQuestions.values().next().value!)
    }
  }

  const changed = (change: Change) => {
    if (loading > 0) changes.set(`${change.kind}:${change.requestID}`, change)
  }

  return {
    /** A machine snapshot has replaced the requests this page started with, which may be a cache's. */
    get confirmed() {
      return confirmed()
    },
    resolveQuestion(request: Pick<QuestionRequest, "id" | "sessionID">) {
      resolved(request.id)
      changed({ kind: "question", revision: ++revision, sessionID: request.sessionID, requestID: request.id })
      applyRequestEvent({
        ...input,
        event: { type: "question.rejected", properties: { sessionID: request.sessionID, requestID: request.id } },
      })
    },
    /** Notes an event before it is applied; false drops it. */
    record(event: Event | { type: typeof REQUEST_ROUTE_EVENT; properties: unknown }) {
      if (event.type === REQUEST_ROUTE_EVENT) {
        const sessionID = (event.properties as { sessionID?: unknown } | undefined)?.sessionID
        if (typeof sessionID === "string" && loading > 0) routed.set(sessionID, ++revision)
      } else if (event.type === "permission.asked") {
        const request = event.properties
        changed({ kind: "permission", revision: ++revision, sessionID: request.sessionID, requestID: request.id, request })
        if (loading > 0 && carriesRoute(request) && !input.store.permission[request.sessionID]?.some((item) => item.id === request.id)) {
          routed.set(request.sessionID, ++revision)
        }
      } else if (event.type === "permission.replied") {
        changed({ kind: "permission", revision: ++revision, sessionID: event.properties.sessionID, requestID: event.properties.requestID })
      } else if (event.type === "question.asked") {
        const request = event.properties
        if (resolvedQuestions.has(request.id)) return false
        changed({ kind: "question", revision: ++revision, sessionID: request.sessionID, requestID: request.id, request })
        // A replay of a request already held brings no route; see applyRequestEvent.
        if (loading > 0 && carriesRoute(request) && !input.store.question[request.sessionID]?.some((item) => item.id === request.id)) {
          routed.set(request.sessionID, ++revision)
        }
      } else if (event.type === "question.replied" || event.type === "question.rejected") {
        resolved(event.properties.requestID)
        changed({ kind: "question", revision: ++revision, sessionID: event.properties.sessionID, requestID: event.properties.requestID })
      }
      return true
    },
    /**
     * Replaces every request and route with load's snapshot, keeping changes
     * that arrived while it loaded. Resolves false when a later refresh
     * superseded it.
     */
    async refresh(load: () => Promise<OpenRequests>) {
      const request = ++latest
      const since = revision
      loading += 1
      // A later refresh's snapshot needs only changes newer than this one's;
      // once none is loading, no change is needed at all.
      const finish = () => {
        loading -= 1
        if (loading === 0) {
          changes.clear()
          routed.clear()
          return
        }
        for (const [key, change] of changes) {
          if (change.revision <= since) changes.delete(key)
        }
        for (const [sessionID, at] of routed) {
          if (at <= since) routed.delete(sessionID)
        }
      }
      let snapshot: OpenRequests
      try {
        snapshot = await load()
      } catch (error) {
        finish()
        throw error
      }
      if (request !== latest) {
        finish()
        return false
      }

      const permissions = bySession(snapshot.permissions)
      const questions = bySession(snapshot.questions.filter((item) => !resolvedQuestions.has(item.id)))
      for (const change of changes.values()) {
        if (change.revision <= since) continue
        if (change.kind === "question") applyMutation(questions, change)
        else applyMutation(permissions, change)
      }
      batch(() => {
        for (const sessionID of new Set([...Object.keys(input.store.permission), ...Object.keys(permissions)])) {
          input.setStore("permission", sessionID, reconcile(sorted(permissions[sessionID]), { key: "id" }))
        }
        for (const sessionID of new Set([...Object.keys(input.store.question), ...Object.keys(questions)])) {
          input.setStore("question", sessionID, reconcile(sorted(questions[sessionID]), { key: "id" }))
        }
        // Only chats with open requests keep a route. A route that arrived
        // while the snapshot loaded is newer than the snapshot's.
        const snapshotRoutes = new Map(snapshot.routes.map((route) => [route.sessionID, route]))
        for (const sessionID of Object.keys(input.store.request_route)) {
          if (!questions[sessionID]?.length && !permissions[sessionID]?.length) input.setStore("request_route", sessionID, undefined)
        }
        for (const [sessionID, route] of snapshotRoutes) {
          const open = !!questions[sessionID]?.length || !!permissions[sessionID]?.length
          if (open && (routed.get(sessionID) ?? 0) <= since) input.setStore("request_route", sessionID, reconcile(route))
        }
        setConfirmed(true)
      })
      finish()
      return true
    },
  }
}

export type RequestSnapshotController = ReturnType<typeof createRequestSnapshotController>
